import { supabase } from '../supabaseClient';

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  sender_type: 'soy_iyire' | 'busco_iyire';
  content: string;
  message_type: 'text' | 'image' | 'location' | 'file';
  created_at: string;
  read: boolean;
  metadata?: {
    image_url?: string;
    location?: { lat: number; lng: number; address: string };
    file_name?: string;
    file_size?: string;
  };
}

export interface ChatConversation {
  id: string;
  participant1_id: string;
  participant1_name: string;
  participant1_type: 'soy_iyire' | 'busco_iyire';
  participant2_id: string;
  participant2_name: string;
  participant2_type: 'soy_iyire' | 'busco_iyire';
  job_id?: string;
  job_title?: string;
  created_at: string;
  updated_at: string;
  last_message?: ChatMessage;
  unread_count_participant1: number;
  unread_count_participant2: number;
}

class ChatService {
  // Obtener conversaciones del usuario
  async getConversations(userId: string): Promise<ChatConversation[]> {
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        console.warn('Offline: omitiendo fetch de conversaciones');
        return [];
      }

      const { data, error } = await supabase
        .from('chat_conversations')
        .select(`
          *,
          last_message:chat_messages!chat_messages_conversation_id_fkey(
            id,
            content,
            created_at,
            sender_name,
            sender_id,
            sender_type,
            message_type,
            read,
            conversation_id
          )
        `)
        .or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`)
        // Traer solo el último mensaje por conversación
        .order('created_at', { foreignTable: 'chat_messages', ascending: false })
        .limit(1, { foreignTable: 'chat_messages' })
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      const err = error as any;
      if (err && err.name === 'TypeError') {
        console.warn('Network error fetching conversations:', err);
      } else {
        console.error('Error fetching conversations:', err);
      }
      return [];
    }
  }

  // Obtener mensajes de una conversación
  async getMessages(conversationId: string): Promise<ChatMessage[]> {
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        console.warn('Offline: omitiendo fetch de mensajes');
        return [];
      }

      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      const err = error as any;
      if (err && err.name === 'TypeError') {
        console.warn('Network error fetching messages:', err);
      } else {
        console.error('Error fetching messages:', err);
      }
      return [];
    }
  }

  // Enviar mensaje
  async sendMessage(
    conversationId: string,
    senderId: string,
    senderName: string,
    senderType: 'soy_iyire' | 'busco_iyire',
    content: string,
    messageType: 'text' | 'image' | 'location' | 'file' = 'text',
    metadata?: any
  ): Promise<ChatMessage | null> {
    console.log('💾 CHAT SERVICE - Enviando mensaje:', {
      conversationId,
      senderId,
      senderName,
      senderType,
      content,
      messageType
    });

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert([{
          conversation_id: conversationId,
          sender_id: senderId,
          sender_name: senderName,
          sender_type: senderType,
          content,
          message_type: messageType,
          read: false,
          metadata
        }])
        .select()
        .single();

      console.log('📊 Respuesta de Supabase:', { data, error });

      if (error) {
        console.error('❌ Error de Supabase:', error);
        throw error;
      }

      console.log('✅ Mensaje guardado en BD:', data);

      // Actualizar timestamp de la conversación
      const { error: updateError } = await supabase
        .from('chat_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId);

      if (updateError) {
        console.error('⚠️ Error actualizando conversación:', updateError);
      } else {
        console.log('✅ Conversación actualizada');
      }

      return data;
    } catch (error) {
      console.error('❌ Error sending message:', error);
      return null;
    }
  }

  // Crear nueva conversación
  async createConversation(
    participant1Id: string,
    participant1Name: string,
    participant1Type: 'soy_iyire' | 'busco_iyire',
    participant2Id: string,
    participant2Name: string,
    participant2Type: 'soy_iyire' | 'busco_iyire',
    jobId?: string,
    jobTitle?: string
  ): Promise<ChatConversation | null> {
    try {
      // Verificar si ya existe una conversación entre estos usuarios
      const { data: existing } = await supabase
        .from('chat_conversations')
        .select('*')
        .or(`and(participant1_id.eq.${participant1Id},participant2_id.eq.${participant2Id}),and(participant1_id.eq.${participant2Id},participant2_id.eq.${participant1Id})`)
        .single();

      if (existing) {
        return existing;
      }

      const { data, error } = await supabase
        .from('chat_conversations')
        .insert([{
          participant1_id: participant1Id,
          participant1_name: participant1Name,
          participant1_type: participant1Type,
          participant2_id: participant2Id,
          participant2_name: participant2Name,
          participant2_type: participant2Type,
          job_id: jobId,
          job_title: jobTitle,
          unread_count_participant1: 0,
          unread_count_participant2: 0
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }

  // Marcar mensajes como leídos
  async markMessagesAsRead(conversationId: string, userId: string): Promise<void> {
    try {
      // Marcar mensajes como leídos
      await supabase
        .from('chat_messages')
        .update({ read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', userId);

      // Resetear contador de no leídos
      const { data: conversation } = await supabase
        .from('chat_conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      if (conversation) {
        const isParticipant1 = conversation.participant1_id === userId;
        const updateField = isParticipant1 ? 'unread_count_participant1' : 'unread_count_participant2';
        
        await supabase
          .from('chat_conversations')
          .update({ [updateField]: 0 })
          .eq('id', conversationId);
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }

  // Suscribirse a nuevos mensajes
  subscribeToMessages(conversationId: string, callback: (message: ChatMessage) => void) {
    return supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          callback(payload.new as ChatMessage);
        }
      )
      .subscribe();
  }

  // Suscribirse a conversaciones
  subscribeToConversations(userId: string, callback: (conversation: ChatConversation) => void) {
    return supabase
      .channel(`conversations:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_conversations',
          filter: `or(participant1_id.eq.${userId},participant2_id.eq.${userId})`
        },
        (payload) => {
          callback(payload.new as ChatConversation);
        }
      )
      .subscribe();
  }

  // Eliminar conversación (y sus mensajes por cascade)
  async deleteConversation(conversationId: string): Promise<void> {
    await supabase
      .from('chat_conversations')
      .delete()
      .eq('id', conversationId);
  }
}

export const chatService = new ChatService();
